import React, { lazy } from 'react';
import { Layout } from '../components/layout/Layout';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { MapPin, Car, Shirt, Clock, Phone, MessageCircle, Youtube, Facebook, Heart, ArrowRight, Navigation, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
const faqs = [{
  icon: Navigation,
  question: 'How do I get there?',
  answer: 'Just add our address to any GPS system and in no time, you will be here. See you soon.'
}, {
  icon: Car,
  question: 'Where should I park?',
  answer: 'On our campus, parking is not a problem at all, and it is free parking. Come and see.'
}, {
  icon: Shirt,
  question: 'What should I wear?',
  answer: 'Our church has a "come as you are" atmosphere. You will see some dressed up dapper and many dressed casual. As long as your attire is decent and you are comfortable in what you are wearing, that\'s all that matters. In our Church, we have no peculiar rules for anyone as regards dressing; decency is the key.'
}];
export function PlanYourVisitPage() {
  return <Layout data-id="element-969">
      {/* Hero Section */}
      <div className="relative bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-white overflow-hidden" data-id="element-970">
        <div className="absolute inset-0 opacity-20" data-id="element-971">
          <img src="https://images.unsplash.com/photo-1438232992991-995b7058bbb3?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Background" className="w-full h-full object-cover" data-id="element-972" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/80 to-[#0A1F44]" data-id="element-973" />

        <div className="container-custom relative z-10" data-id="element-974">
          <div className="max-w-3xl" data-id="element-975">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block text-[#C8102E] text-sm font-semibold tracking-widest uppercase mb-4" data-id="element-976">
              Welcome Home
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-6xl font-serif font-bold mb-6" data-id="element-977">
              Plan Your Visit
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-white/80 leading-relaxed max-w-2xl" data-id="element-978">
              We can't wait to meet you! Here's everything you need to know
              before you arrive at The Household.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Welcome Message */}
      <section className="py-20 bg-white" data-id="element-979">
        <div className="container-custom" data-id="element-980">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-981">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-982">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-983">
                A Place for You
              </h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed" data-id="element-984">
                <p data-id="element-985">
                  Visiting a new church can be intimidating, but we want to make
                  sure you feel right at home from the moment you pull into the
                  parking lot.
                </p>
                <p data-id="element-986">
                  At The Household, you'll find a diverse community of believers
                  who are passionate about God and welcoming to all. Whether
                  you've grown up in church or are exploring faith for the first
                  time, there's a place for you here.
                </p>
                <p data-id="element-987">
                  Our services feature passionate, Spirit-led worship and
                  practical, biblical teaching that you can apply to your
                  everyday life.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-988">
              <div className="bg-[#F5F7FA] rounded-2xl p-8 md:p-10" data-id="element-989">
                <Heart className="w-12 h-12 text-[#C8102E] mb-6" data-id="element-990" />
                <p className="text-xl font-serif text-[#0A1F44] leading-relaxed italic" data-id="element-991">
                  "No matter where you are now in your walk with God, we believe
                  that it can be sweeter and better, if you wholeheartedly
                  embrace the grace and mercy of God, through Jesus Christ, The
                  Lord of The Household."
                </p>
                <p className="mt-6 text-[#0A1F44] font-semibold" data-id="element-992">— Shalom</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-[#0A1F44] text-white" data-id="element-993">
        <div className="container-custom" data-id="element-994">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-3xl mx-auto text-center" data-id="element-995">
            <span className="text-[#C8102E] text-sm font-semibold tracking-widest uppercase mb-4 block" data-id="element-996">
              First Time Here?
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6" data-id="element-997">
              What to Expect
            </h2>
            <p className="text-white/80 text-lg mb-12" data-id="element-998">
              Our services are designed to help you encounter God and connect
              with others.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-999">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-[#163A70] p-8 rounded-2xl text-center text-white" data-id="element-1000">
              <Clock className="w-10 h-10 text-[#C8102E] mx-auto mb-4" data-id="element-1001" />
              <h3 className="text-xl font-serif font-bold mb-2" data-id="element-1002">
                Sunday Service
              </h3>
              <p className="text-white/80" data-id="element-1003">10:00 AM EST</p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-[#163A70] p-8 rounded-2xl text-center text-white" data-id="element-1004">
              <Clock className="w-10 h-10 text-[#C8102E] mx-auto mb-4" data-id="element-1005" />
              <h3 className="text-xl font-serif font-bold mb-2" data-id="element-1006">
                Impact Wednesday
              </h3>
              <p className="text-white/80" data-id="element-1007">7:00 PM EST</p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-[#163A70] p-8 rounded-2xl text-center text-white" data-id="element-1008">
              <Clock className="w-10 h-10 text-[#C8102E] mx-auto mb-4" data-id="element-1009" />
              <h3 className="text-xl font-serif font-bold mb-2" data-id="element-1010">Deep Dive</h3>
              <p className="text-white/80" data-id="element-1011">Sunday 9:00 AM EST</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-[#F5F7FA]" data-id="element-1012">
        <div className="container-custom" data-id="element-1013">
          <SectionHeading title="Common Questions" subtitle="FAQ" data-id="element-1014" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1015">
            {faqs.map((faq, index) => <motion.div key={faq.question} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm" data-id="element-1016">
                <div className="w-14 h-14 bg-[#0A1F44] rounded-full flex items-center justify-center mb-6" data-id="element-1017">
                  <faq.icon className="w-7 h-7 text-white" data-id="element-1018" />
                </div>
                <h3 className="text-xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-1019">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1020">{faq.answer}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-20 bg-white" data-id="element-1021">
        <div className="container-custom" data-id="element-1022">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1023">
            {/* Map/Location */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gray-100 rounded-2xl overflow-hidden min-h-[400px] relative" data-id="element-1024">
              {/* Placeholder for actual map */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-200" data-id="element-1025">
                <MapPin className="w-12 h-12 text-[#C8102E] mb-4" data-id="element-1026" />
                <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-1027">
                  The Household
                </h3>
                <p className="text-gray-600 text-center" data-id="element-1028">
                  1221 Caraway Ct, #1090
                  <br data-id="element-1029" />
                  Largo, MD 20774
                </p>
                <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="mt-6" data-id="element-1030">
                  <Button variant="primary" data-id="element-1031">Get Directions</Button>
                </a>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-1032">
              <span className="text-[#C8102E] text-sm font-semibold tracking-widest uppercase mb-4 block" data-id="element-1033">
                Get In Touch
              </span>
              <h2 className="text-3xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-1034">
                We'd Love to Hear From You
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-1035">
                After your tour of our website, do not hesitate to leave a
                comment or you can also call or send us a text. We look forward
                to reading from you.
              </p>

              <div className="space-y-4 mb-8" data-id="element-1036">
                <a href="tel:301-996-6907" className="flex items-center gap-4 p-4 bg-[#F5F7FA] rounded-xl hover:bg-[#e2e8f0] transition-colors" data-id="element-1037">
                  <div className="w-12 h-12 bg-[#0A1F44] rounded-full flex items-center justify-center" data-id="element-1038">
                    <Phone className="w-5 h-5 text-white" data-id="element-1039" />
                  </div>
                  <div data-id="element-1040">
                    <p className="text-sm text-gray-500" data-id="element-1041">Call or Text</p>
                    <p className="text-[#0A1F44] font-semibold" data-id="element-1042">
                      (301) 996-6907
                    </p>
                  </div>
                </a>

                <a href="mailto:Info@householdchurch.org" className="flex items-center gap-4 p-4 bg-[#F5F7FA] rounded-xl hover:bg-[#e2e8f0] transition-colors" data-id="element-1043">
                  <div className="w-12 h-12 bg-[#C8102E] rounded-full flex items-center justify-center" data-id="element-1044">
                    <Mail className="w-5 h-5 text-white" data-id="element-1045" />
                  </div>
                  <div data-id="element-1046">
                    <p className="text-sm text-gray-500" data-id="element-1047">Email Us</p>
                    <p className="text-[#0A1F44] font-semibold" data-id="element-1048">
                      Info@householdchurch.org
                    </p>
                  </div>
                </a>

                <Link to="/contact" className="flex items-center gap-4 p-4 bg-[#F5F7FA] rounded-xl hover:bg-[#e2e8f0] transition-colors" data-id="element-1049">
                  <div className="w-12 h-12 bg-[#0A1F44] rounded-full flex items-center justify-center" data-id="element-1050">
                    <MessageCircle className="w-5 h-5 text-white" data-id="element-1051" />
                  </div>
                  <div data-id="element-1052">
                    <p className="text-sm text-gray-500" data-id="element-1053">Send a Message</p>
                    <p className="text-[#0A1F44] font-semibold" data-id="element-1054">Contact Form</p>
                  </div>
                </Link>
              </div>

              {/* Can't Visit In Person */}
              <div className="bg-[#0A1F44] p-6 rounded-2xl" data-id="element-1055">
                <h3 className="text-white font-serif font-bold mb-3" data-id="element-1056">
                  Can't visit in person?
                </h3>
                <p className="text-gray-400 text-sm mb-4" data-id="element-1057">
                  Join us online via our livestream platforms
                </p>
                <div className="flex gap-3" data-id="element-1058">
                  <a href="https://youtube.com/@householdchurch" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors text-sm" data-id="element-1059">
                    <Youtube className="w-4 h-4" data-id="element-1060" />
                    YouTube
                  </a>
                  <a href="https://facebook.com/householdchurch" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors text-sm" data-id="element-1061">
                    <Facebook className="w-4 h-4" data-id="element-1062" />
                    Facebook
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#C8102E]" data-id="element-1063">
        <div className="container-custom text-center" data-id="element-1064">
          <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-1065">
            Ready to Take the Next Step?
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto" data-id="element-1066">
            We can't wait to meet you! Join us this Sunday and experience the
            love of God.
          </p>
          <Link to="/contact" data-id="element-1067">
            <Button variant="white" size="lg" className="text-[#C8102E]" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-1069" />} data-id="element-1068">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}